/**
 * Offline queue service for handling actions when offline
 * Stores actions locally and replays them when back online
 */

import type { Clock } from '@/lib/clock'
import { systemClock } from '@/lib/clock'
import type { IdGenerator } from '@/lib/id'
import { randomIdGenerator } from '@/lib/id'
import { getBrowserAdapter } from '@/lib/browserAdapter'
import { browserStorage, STORAGE_KEYS } from '@/lib/storage'

interface QueuedAction {
    id: string
    type: 'advance_pointer'
    data: {
        storyId: string
        sentenceSeq: number
        timestamp: number
    }
}

class OfflineQueueService {
    private queue: QueuedAction[] = []
    private isOnline: boolean = typeof navigator !== 'undefined' ? navigator.onLine : true
    private initialized = false
    private clock: Clock
    private idGenerator: IdGenerator

    constructor(clock: Clock = systemClock, idGenerator: IdGenerator = randomIdGenerator) {
        this.clock = clock
        this.idGenerator = idGenerator
    }

    // Bound handlers for event listener cleanup
    private handleOnline = () => {
        this.isOnline = true
        void this.processQueue()
    }

    private handleOffline = () => {
        this.isOnline = false
    }

    /**
     * Initialize the service by registering event listeners and loading persisted queue.
     * Must be called before the service can respond to online/offline events.
     * Safe to call multiple times - will only initialize once.
     */
    initialize(): void {
        if (this.initialized) return
        const browserAdapter = getBrowserAdapter()

        browserAdapter.addEventListener('online', this.handleOnline)
        browserAdapter.addEventListener('offline', this.handleOffline)
        this.loadQueue()
        this.initialized = true
    }

    /**
     * Destroy the service by removing event listeners.
     * Call this when the service is no longer needed (e.g., in cleanup effects).
     */
    destroy(): void {
        const browserAdapter = getBrowserAdapter()

        browserAdapter.removeEventListener('online', this.handleOnline)
        browserAdapter.removeEventListener('offline', this.handleOffline)
        this.initialized = false
    }

    /**
     * Check if the service has been initialized.
     */
    isInitialized(): boolean {
        return this.initialized
    }

    /**
     * Queue an auto-advance action for when back online
     */
    queueAdvancePointer(storyId: string, sentenceSeq: number): string {
        const now = this.clock.now()
        const actionId = `advance_${now}_${this.idGenerator.generate()}`

        const action: QueuedAction = {
            id: actionId,
            type: 'advance_pointer',
            data: {
                storyId,
                sentenceSeq,
                timestamp: now,
            },
        }

        this.queue.push(action)
        this.saveQueue()

        console.log('Queued auto-advance action for offline processing:', action)
        return actionId
    }

    /**
     * Process all queued actions when back online
     */
    private async processQueue() {
        if (!this.isOnline || this.queue.length === 0) {
            return
        }

        console.log(`Processing ${this.queue.length} queued actions...`)

        const actionsToProcess = [...this.queue]
        this.queue = []
        this.saveQueue()

        for (const action of actionsToProcess) {
            try {
                await this.processAction(action)
            } catch (error) {
                console.error('Failed to process queued action:', action, error)
                // Re-queue failed actions
                this.queue.push(action)
            }
        }

        this.saveQueue()
    }

    /**
     * Process a single queued action
     */
    private async processAction(action: QueuedAction) {
        switch (action.type) {
            case 'advance_pointer': {
                // Dynamic import to avoid circular dependency between offline-queue and stories.service.
                // Vite warns this won't create a separate chunk since stories.service is statically
                // imported elsewhere — that's expected; the dynamic import is for initialization ordering.
                const { updateStoryProgress } = await import('./stories.service')
                await updateStoryProgress(action.data.storyId, action.data.sentenceSeq)
                console.log('Processed queued advance pointer action:', action)
                break
            }
            default:
                console.warn('Unknown action type:', action.type)
        }
    }

    /**
     * Save queue to storage
     */
    private saveQueue() {
        try {
            browserStorage.setItem(STORAGE_KEYS.OFFLINE_QUEUE, JSON.stringify(this.queue))
        } catch (error) {
            console.error('Failed to save offline queue:', error)
        }
    }

    /**
     * Load queue from storage
     */
    private loadQueue() {
        try {
            const stored = browserStorage.getItem(STORAGE_KEYS.OFFLINE_QUEUE)
            if (stored) {
                this.queue = JSON.parse(stored) as QueuedAction[]
                console.log(`Loaded ${this.queue.length} queued actions from storage`)
            }
        } catch (error) {
            console.error('Failed to load offline queue:', error)
            this.queue = []
        }
    }

    /**
     * Get current queue status
     */
    getQueueStatus() {
        return {
            isOnline: this.isOnline,
            queueLength: this.queue.length,
            actions: this.queue,
        }
    }

    /**
     * Clear all queued actions
     */
    clearQueue() {
        this.queue = []
        this.saveQueue()
    }
}

/**
 * Factory function to create a new OfflineQueueService instance.
 * Use this in tests for fresh, isolated instances.
 * Remember to call initialize() on the returned instance.
 *
 * @param clock - Clock for time operations (defaults to systemClock)
 * @param idGenerator - ID generator for action IDs (defaults to randomIdGenerator)
 */
export const createOfflineQueueService = (
    clock: Clock = systemClock,
    idGenerator: IdGenerator = randomIdGenerator
): OfflineQueueService => new OfflineQueueService(clock, idGenerator)

/**
 * Default singleton instance for production use.
 * Note: Call offlineQueueService.initialize() during app startup.
 */
export const offlineQueueService = createOfflineQueueService()

export { OfflineQueueService }
